import { type ComponentProps, type JSX, useState } from "react";

import type { MobileSelectionListItem } from "common/components/MobileSelectionList/index";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import Checkbox from "common/components/ui/Checkbox";
import { typedObjectEntries } from "common/helpers/utils";

import TagSelectionDrawer from "./TagSelectionDrawer";

interface MobileSelectionListProps<
  DataType extends { id: string },
  TagKey extends string,
> {
  data: DataType[];
  selection: DataType[];
  onSelectionChange: (selection: "all" | DataType[]) => void;
  allTags: Record<
    TagKey,
    {
      icon: JSX.Element;
      label: string;
      renderTag: (data: DataType) => JSX.Element;
      isDefault?: true;
    }
  >;
  totalDataCount: number;
  children: (
    item: DataType,
    listItemProps: Pick<
      ComponentProps<typeof MobileSelectionListItem>,
      "isSelected" | "isSelecting" | "toggleIsSelected" | "badgeContent"
    >,
  ) => JSX.Element;
}

export default function MobileSelectionList<
  DataType extends { id: string },
  TagKey extends string,
>({
  data,
  selection,
  onSelectionChange,
  allTags,
  totalDataCount,
  children,
}: MobileSelectionListProps<DataType, TagKey>) {
  const [isSelecting, setIsSelecting] = useState(false);
  const [visibleTagKeys, setVisibleTagKeys] = useState<TagKey[]>(
    typedObjectEntries(allTags)
      .filter(([, { isDefault }]) => isDefault)
      .map(([key]) => key),
  );

  const hasSelection = !!selection.length;
  //true if all the items in the current page are selected
  const isAllSelected = selection.length === totalDataCount;

  let checkboxState: boolean | "indeterminate" = false;
  if (isAllSelected) {
    checkboxState = true;
  } else if (hasSelection) {
    checkboxState = "indeterminate";
  }

  return (
    <div className="flex flex-col -mx-2">
      <div className="border-y border-y-black-200 p-2 gap-x-1.5 flex items-center mb-1">
        <RenderIf condition={isSelecting}>
          <Checkbox
            checked={checkboxState}
            onCheckedChange={() => {
              if (hasSelection) {
                onSelectionChange([]);
              } else {
                onSelectionChange(data);
              }
            }}
          />
        </RenderIf>
        <span className="text-button-xs text-black-500">
          <RenderIf condition={isSelecting}>
            <span className="text-black-950">{selection.length}</span> of{" "}
            {totalDataCount}
          </RenderIf>
        </span>
        <RenderIf condition={hasSelection}>
          {selection.length === totalDataCount ? (
            <Button
              variant="link-purple"
              size="sm"
              onClick={() => onSelectionChange([])}
            >
              Clear selection
            </Button>
          ) : (
            <Button
              variant="link-purple"
              size="sm"
              onClick={() => onSelectionChange("all")}
            >
              Select all
            </Button>
          )}
        </RenderIf>
        <Button
          variant="secondary-purple"
          className="ml-auto"
          size="sm"
          onClick={() => {
            if (isSelecting) {
              onSelectionChange([]);
            }
            setIsSelecting(!isSelecting);
          }}
        >
          {isSelecting ? "Cancel" : "Select"}
        </Button>
        <TagSelectionDrawer
          tagKeys={visibleTagKeys}
          setTagKeys={setVisibleTagKeys}
          allTags={allTags}
        />
      </div>
      <div className="flex flex-col">
        {data.map((item) => {
          const isSelected = !!selection.find(
            (selectedItem) => selectedItem.id === item.id,
          );
          return children(item, {
            isSelecting,
            isSelected,
            toggleIsSelected: () => {
              if (isSelected) {
                onSelectionChange(
                  selection.filter(
                    (selectedItem) => selectedItem.id !== item.id,
                  ),
                );
              } else {
                onSelectionChange([...selection, item]);
              }
            },
            badgeContent: visibleTagKeys.map((key) =>
              allTags[key].renderTag(item),
            ),
          });
        })}
      </div>
    </div>
  );
}
